import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Step,
  Stepper,
  StepLabel,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  CheckCircle,
  FileUpload,
  Description,
  Visibility,
  Settings,
  List,
  Download,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { api } from '../../../services/api';
import { importService } from '../../../services/importService';

interface ImportMensalidadesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Contrato {
  id: number;
  titulo: string;
  cliente_id: number;
  nome_cliente: string;
  cidade: string;
  estado: string;
  usuario_pppoe: string;
  valor_contratado: number;
  status: string;
}

interface ImportResult {
  contrato_id: number;
  cliente_id: number;
  faturas_cadastradas: number;
  faturas_abertas: number;
  faturas_vencidas: number;
  faturas_pagas: number;
}

const steps = [
  'Configuração da API',
  'Listagem de Contratos',
  'Importação'
];

export const ImportMensalidadesModal: React.FC<ImportMensalidadesModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Configuração da API
  const [urlApi, setUrlApi] = useState('https://www.seusite.com.br/admin/addons/sysup/apiv2/listarMensalidadesLogin');
  const [tokenApi, setTokenApi] = useState('9f4c8db4c60a93f1a3a77a9dd11b2f5c49c634b9dcf32776f79c4fc343a3b07d');

  // Listagem de contratos
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [loadingContratos, setLoadingContratos] = useState(false);
  const [selectedContratos, setSelectedContratos] = useState<number[]>([]);

  // Importação
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [importing, setImporting] = useState(false);
  const [currentImportIndex, setCurrentImportIndex] = useState(0);

  // Carregar contratos quando entrar na etapa 1
  useEffect(() => {
    if (activeStep === 1 && (contratos?.length || 0) === 0) {
      loadContratos();
    }
  }, [activeStep]);

  const loadContratos = async () => {
    setLoadingContratos(true);
    setError(null);

    try {
      const response = await api.get('/import/contratos');

      // Verificar se a resposta tem a estrutura esperada
      if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
        setContratos(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        // Fallback: se os dados vierem diretamente no response.data
        setContratos(response.data);
      } else {
        setError('Formato de resposta inesperado da API');
        setContratos([]);
      }
    } catch (err: any) {
      setError('Erro ao carregar contratos: ' + (err.response?.data?.message || err.message));
      setContratos([]);
    } finally {
      setLoadingContratos(false);
    }
  };

  const handleConfigurarApi = () => {
    if (!urlApi.trim() || !tokenApi.trim()) {
      setError('URL e Token da API são obrigatórios');
      return;
    }

    setError(null);
    setActiveStep(1);
  };

  const handleSelectAllContratos = (checked: boolean) => {
    if (checked) {
      setSelectedContratos(contratos.map(c => c.id));
    } else {
      setSelectedContratos([]);
    }
  };

  const handleSelectContrato = (contratoId: number, checked: boolean) => {
    if (checked) {
      setSelectedContratos(prev => [...prev, contratoId]);
    } else {
      setSelectedContratos(prev => prev.filter(id => id !== contratoId));
    }
  };

  const handleIniciarImportacao = async () => {
    if (selectedContratos.length === 0) {
      setError('Selecione pelo menos um contrato para importar');
      return;
    }

    setImporting(true);
    setError(null);
    setImportResults([]);
    setActiveStep(2);

    const contratosSelecionados = contratos.filter(c => selectedContratos.includes(c.id));

    for (let i = 0; i < contratosSelecionados.length; i++) {
      const contrato = contratosSelecionados[i];
      setCurrentImportIndex(i + 1);

      try {
        // Usar o serviço de importação
        const response = await importService.importMensalidades({
          login: contrato.usuario_pppoe,
          cliente_id: contrato.cliente_id,
          contrato_id: contrato.id,
          url_api: urlApi,
          token_api: tokenApi
        });

        // Validar se a resposta tem a estrutura esperada
        if (response.success && response.data) {
          setImportResults(prev => [...prev, response.data]);
          toast.success(`Contrato ${contrato.titulo} importado com sucesso!`);
        } else {
          // Se a resposta não tem a estrutura esperada, criar um resultado padrão
          setImportResults(prev => [...prev, {
            contrato_id: contrato.id,
            cliente_id: contrato.cliente_id,
            faturas_cadastradas: 0,
            faturas_abertas: 0,
            faturas_vencidas: 0,
            faturas_pagas: 0
          }]);
          toast.warning(`Contrato ${contrato.titulo} importado, mas com dados incompletos`);
        }

      } catch (err: any) {

        let errorMessage = 'Erro desconhecido';
        if (err.code === 'ECONNABORTED') {
          errorMessage = 'Timeout: A importação demorou muito tempo. Tente novamente.';
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }

        toast.error(`Erro ao importar contrato ${contrato.titulo}: ${errorMessage}`);

        setImportResults(prev => [...prev, {
          contrato_id: contrato.id,
          cliente_id: contrato.cliente_id,
          faturas_cadastradas: 0,
          faturas_abertas: 0,
          faturas_vencidas: 0,
          faturas_pagas: 0
        }]);
      }
    }

    setImporting(false);
    toast.success('Importação concluída!');
  };

  const handleClose = () => {
    setActiveStep(0);
    setUrlApi('');
    setTokenApi('');
    setContratos([]);
    setSelectedContratos([]);
    setImportResults([]);
    setError(null);
    setLoading(false);
    setImporting(false);
    setCurrentImportIndex(0);
    onClose();
  };

  const renderConfiguracaoStep = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Configure a conexão com a API do MKAuth para importar mensalidades
      </Typography>

      <Card sx={{ mt: 3, p: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuração da API MKAuth
          </Typography>

          <form onSubmit={(e) => e.preventDefault()}>
            <TextField
              fullWidth
              label="URL da API"
              value={urlApi}
              onChange={(e) => setUrlApi(e.target.value)}
              placeholder="https://api.mkauth.com.br"
              margin="normal"
              helperText="URL base da API do MKAuth"
            />

            <TextField
              fullWidth
              label="Token de Acesso"
              value={tokenApi}
              onChange={(e) => setTokenApi(e.target.value)}
              placeholder="Seu token de acesso"
              margin="normal"
              type="password"
              autoComplete="current-password"
              helperText="Token de autenticação da API"
            />
          </form>
        </CardContent>
      </Card>
    </Box>
  );

  const renderListagemStep = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Selecione os contratos que deseja importar mensalidades
      </Typography>

      <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={loadContratos}
          disabled={loadingContratos}
          startIcon={loadingContratos ? <CircularProgress size={20} /> : <List />}
        >
          Recarregar Contratos
        </Button>

        <Typography variant="body2" color="text.secondary">
          {contratos?.length || 0} contratos encontrados
        </Typography>
      </Box>

      {loadingContratos ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedContratos.length === (contratos?.length || 0) && (contratos?.length || 0) > 0}
                    indeterminate={selectedContratos.length > 0 && selectedContratos.length < (contratos?.length || 0)}
                    onChange={(e) => handleSelectAllContratos(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Contrato ID</TableCell>
                <TableCell>Contrato Título</TableCell>
                <TableCell>Nome Cliente</TableCell>
                <TableCell>Cidade</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Usuário PPPoE</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(contratos || []).map((contrato) => (
                <TableRow key={contrato.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedContratos.includes(contrato.id)}
                      onChange={(e) => handleSelectContrato(contrato.id, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{contrato.id}</TableCell>
                  <TableCell>{contrato.titulo}</TableCell>
                  <TableCell>{contrato.nome_cliente}</TableCell>
                  <TableCell>{contrato.cidade}</TableCell>
                  <TableCell>{contrato.estado}</TableCell>
                  <TableCell>{contrato.usuario_pppoe}</TableCell>
                  <TableCell>
                    <Chip
                      label={contrato.status}
                      color={contrato.status === 'ativo' ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const renderImportacaoStep = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Importando mensalidades dos contratos selecionados
      </Typography>

      {importing && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            Processando contrato {currentImportIndex} de {selectedContratos.length}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            A importação pode demorar alguns minutos. Por favor, aguarde...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {importResults.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resultados da Importação
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Contrato</TableCell>
                    <TableCell align="right">Cadastradas</TableCell>
                    <TableCell align="right">Abertas</TableCell>
                    <TableCell align="right">Vencidas</TableCell>
                    <TableCell align="right">Pagas</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importResults.map((result, index) => {
                    // Validar se o resultado existe e tem contrato_id
                    if (!result || typeof result.contrato_id === 'undefined') {
                      return null; // Não renderizar linhas inválidas
                    }

                    const contrato = contratos.find(c => c.id === result.contrato_id);
                    return (
                      <TableRow key={`${result.contrato_id}-${index}`}>
                        <TableCell>{contrato?.titulo || `Contrato ${result.contrato_id}`}</TableCell>
                        <TableCell align="right">{result.faturas_cadastradas || 0}</TableCell>
                        <TableCell align="right">{result.faturas_abertas || 0}</TableCell>
                        <TableCell align="right">{result.faturas_vencidas || 0}</TableCell>
                        <TableCell align="right">{result.faturas_pagas || 0}</TableCell>
                      </TableRow>
                    );
                  }).filter(Boolean)} {/* Remover elementos null */}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderConfiguracaoStep();
      case 1:
        return renderListagemStep();
      case 2:
        return renderImportacaoStep();
      default:
        return null;
    }
  };

  const getActionButtons = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            variant="contained"
            onClick={handleConfigurarApi}
            disabled={!urlApi.trim() || !tokenApi.trim()}
            startIcon={<Settings />}
          >
            Configurar e Continuar
          </Button>
        );
      case 1:
        return (
          <>
            <Button onClick={() => setActiveStep(0)}>
              Voltar
            </Button>
            <Button
              variant="contained"
              onClick={handleIniciarImportacao}
              disabled={selectedContratos.length === 0 || importing}
              startIcon={<Download />}
            >
              Iniciar Importação
            </Button>
          </>
        );
      case 2:
        return (
          <>
            <Button onClick={() => setActiveStep(1)} disabled={importing}>
              Voltar
            </Button>
            <Button
              variant="contained"
              onClick={onSuccess}
              color="success"
            >
              Concluir
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Importar Mensalidades MKAuth
          </Typography>
          <IconButton onClick={handleClose} disabled={importing}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {getStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          Cancelar
        </Button>
        {getActionButtons()}
      </DialogActions>
    </Dialog>
  );
};

export default ImportMensalidadesModal;